import React, { useRef, useEffect } from 'react';

interface IsolatedContentProps {
  html: string;
  className?: string;
}

/**
 * Renders HTML content inside a Shadow DOM to isolate its CSS
 * from affecting the rest of the application.
 */
export const IsolatedContent: React.FC<IsolatedContentProps> = ({ html, className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const shadowRootRef = useRef<ShadowRoot | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Create shadow root if it doesn't exist
    if (!shadowRootRef.current) {
      shadowRootRef.current = containerRef.current.attachShadow({ mode: 'open' });
    }

    // Base styles - restore browser defaults that Tailwind resets
    const styles = `
      <style>
        :host {
          display: block;
          font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
          font-size: 1rem;
          line-height: 1.75;
          color: #1a1a1a;
          background: #ffffff;
          padding: 1.5rem;
          border-radius: 0.5rem;
        }

        /* Headings */
        h1 { font-size: 2em; font-weight: bold; margin: 0.67em 0; }
        h2 { font-size: 1.5em; font-weight: bold; margin: 0.83em 0; }
        h3 { font-size: 1.17em; font-weight: bold; margin: 1em 0; }
        h4 { font-size: 1em; font-weight: bold; margin: 1.33em 0; }
        h5 { font-size: 0.83em; font-weight: bold; margin: 1.67em 0; }
        h6 { font-size: 0.67em; font-weight: bold; margin: 2.33em 0; }

        /* Block elements */
        p { margin: 1em 0; }
        blockquote {
          margin: 1em 40px;
          padding-left: 1em;
          border-left: 4px solid #ddd;
          font-style: italic;
        }

        /* Lists */
        ul { list-style-type: disc; margin: 1em 0; padding-left: 40px; }
        ol { list-style-type: decimal; margin: 1em 0; padding-left: 40px; }
        li { display: list-item; margin: 0.5em 0; }
        ul ul { list-style-type: circle; }
        ul ul ul { list-style-type: square; }

        /* Links */
        a { color: #0066cc; text-decoration: underline; }
        a:hover { color: #004499; }

        /* Images */
        img { max-width: 100%; height: auto; margin: 1em 0; }
        figure { margin: 1em 0; }
        figcaption { font-size: 0.875em; color: #666; text-align: center; margin-top: 0.5em; }

        /* Code */
        pre {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          background: #f5f5f5;
          padding: 1em;
          overflow-x: auto;
          border-radius: 4px;
          margin: 1em 0;
        }
        code {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          background: #f5f5f5;
          padding: 0.2em 0.4em;
          border-radius: 3px;
          font-size: 0.9em;
        }
        pre code { background: none; padding: 0; }

        /* Tables */
        table { border-collapse: collapse; width: 100%; margin: 1em 0; }
        th, td { border: 1px solid #ddd; padding: 0.75em; text-align: left; }
        th { background: #f5f5f5; font-weight: bold; }

        /* Horizontal rule */
        hr { border: none; border-top: 1px solid #ddd; margin: 2em 0; }

        /* Text formatting */
        strong, b { font-weight: bold; }
        em, i { font-style: italic; }
        u { text-decoration: underline; }
        s, strike, del { text-decoration: line-through; }
        sub { vertical-align: sub; font-size: smaller; }
        sup { vertical-align: super; font-size: smaller; }
        small { font-size: smaller; }
        mark { background: #ff0; padding: 0.1em 0.2em; }

        /* Definition lists */
        dl { margin: 1em 0; }
        dt { font-weight: bold; }
        dd { margin-left: 40px; }

        /* Address */
        address { font-style: italic; margin: 1em 0; }
      </style>
    `;

    // Update shadow root content
    shadowRootRef.current.innerHTML = `${styles}${html}`;
  }, [html]);

  return <div ref={containerRef} className={className} />;
};

export default IsolatedContent;
